// Formstack appends its own field id to every field name ("client_full_name_6135").
const FORMSTACK_ID_SUFFIX = /\s+\d{3,}$/;

const YES_NO_FIELD = /^(.*)_(yes|no)(?:_\d+)?$/i;

const YES_NO_QUESTIONS = {
  attorney_referral: {
    label: "Was the client referred by another attorney?",
    hint: "Filled in from the client's source in Salesforce. Change it only if that's wrong.",
  },
};

export const YES_NO_PAIR = "yesNoPair";

const cleanLabel = (field) =>
  typeof field.label === "string" && FORMSTACK_ID_SUFFIX.test(field.label)
    ? { ...field, label: field.label.replace(FORMSTACK_ID_SUFFIX, "") }
    : field;

const humanQuestion = (stem) => {
  const words = stem.replace(/_/g, " ").trim();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}?`;
};

/**
 * Templates answer some yes/no questions with two boxes ("…_yes_6136" and
 * "…_no_6138"), exactly one of which holds "Yes". Show the pair as one
 * Yes/No question instead of two text boxes.
 */
const combineYesNoPairs = (fields) => {
  const byStem = {};
  for (const field of fields) {
    if (!field || field.type !== "text" || typeof field.name !== "string") continue;
    const match = field.name.match(YES_NO_FIELD);
    if (!match) continue;
    const stem = match[1].toLowerCase();
    byStem[stem] = { ...byStem[stem], [match[2].toLowerCase()]: field };
  }

  const pairs = {};
  for (const [stem, pair] of Object.entries(byStem)) {
    if (pair.yes && pair.no) pairs[stem] = pair;
  }
  if (!Object.keys(pairs).length) return fields;

  const result = [];
  for (const field of fields) {
    const match = field?.type === "text" && typeof field.name === "string"
      ? field.name.match(YES_NO_FIELD)
      : null;
    const stem = match?.[1].toLowerCase();
    const pair = stem && pairs[stem];
    if (!pair) {
      result.push(field);
      continue;
    }
    if (field !== pair.yes && field !== pair.no) continue;
    if (result.some((item) => item.type === YES_NO_PAIR && item.stem === stem)) continue;
    const isYes = (value) => String(value || "").trim().toLowerCase() === "yes";
    const question = YES_NO_QUESTIONS[stem] || {};
    result.push({
      type: YES_NO_PAIR,
      key: `${YES_NO_PAIR}:${stem}`,
      stem,
      label: question.label || humanQuestion(stem),
      hint: question.hint || "",
      yesKey: pair.yes.key,
      noKey: pair.no.key,
      value: isYes(pair.yes.value) ? "yes" : isYes(pair.no.value) ? "no" : "",
    });
  }
  return result;
};

export const preprocessFormFields = (fields) => {
  try {
    if (!Array.isArray(fields)) return [];

    fields = combineYesNoPairs(
      fields.map((field) => (field && typeof field === "object" ? cleanLabel(field) : field))
    );

    const grouped = [];
    let tempCheckboxes = [];

    const isCheckbox = (f) => f && f.type === "checkbox";

    const flushCheckboxGroup = () => {
      if (tempCheckboxes.length > 1) {
        grouped.push({ type: "checkboxGroup", fields: [...tempCheckboxes] });
      } else if (tempCheckboxes.length === 1) {
        grouped.push(tempCheckboxes[0]);
      }
      tempCheckboxes = [];
    };

    for (const field of fields) {
      if (!field || typeof field !== "object") continue;

      // Normalize radio buttons safely
      if (
        field.type === "radio" &&
        Array.isArray(field.exportValues) &&
        Array.isArray(field.labels) &&
        field.exportValues.length === field.labels.length
      ) {
        const clonedField = { ...field };
        clonedField.options = field.exportValues.map((val, i) => ({
          label: field.labels[i],
          value: val,
        }));

        flushCheckboxGroup();
        grouped.push(clonedField);
        continue;
      }

      // Group checkboxes by name or prefix
      if (isCheckbox(field)) {
        tempCheckboxes.push(field);
        continue;
      }

      // Flush and push normal field
      flushCheckboxGroup();
      grouped.push(field);
    }

    flushCheckboxGroup();
    return grouped;
  } catch (e) {
    console.error("preprocessFormFields failed:", e);
    return [];
  }
};
